import { useCallback, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
  Input,
  Label,
} from "@repo/ui";
import { ArrowLeft, CheckCircle, Loader2, Mail } from "lucide-react";
import {
  forgotStaffPasswordEligibility,
  forgotStaffPassword,
  verifyStaffReset,
} from "@/lib/api/auth";
import { SecureInput } from "@/components/forms";

export const Route = createFileRoute("/_auth/forgot-staff-password")({
  component: ForgotStaffPasswordPage,
  validateSearch: (search: Record<string, unknown>): { initials?: string } => ({
    initials: (search.initials as string) ?? undefined,
  }),
});

type PageState = "idle" | "confirm" | "code_sent" | "success";

function ForgotStaffPasswordPage() {
  const navigate = useNavigate();
  const { initials: searchInitials } = Route.useSearch();

  const [initialsValue, setInitialsValue] = useState(searchInitials ?? "");
  const [pageState, setPageState] = useState<PageState>("idle");
  const [obfuscatedEmail, setObfuscatedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [notEligibleMessage, setNotEligibleMessage] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCheckEligibility = useCallback(async () => {
    const normalized = initialsValue.toUpperCase().trim();
    if (normalized.length < 2) return;

    setNotEligibleMessage("");
    setError("");
    setIsChecking(true);

    try {
      const data = (await forgotStaffPasswordEligibility(normalized)) as {
        eligible?: boolean;
      };

      if (data.eligible) {
        setPageState("confirm");
      } else {
        setNotEligibleMessage(
          "Please contact your administrator to reset your password."
        );
      }
    } catch {
      setNotEligibleMessage("Network error. Please try again.");
    } finally {
      setIsChecking(false);
    }
  }, [initialsValue]);

  const handleSendCode = useCallback(async () => {
    const normalized = initialsValue.toUpperCase().trim();
    setError("");
    setIsSending(true);

    try {
      const data = (await forgotStaffPassword({ initials: normalized })) as {
        eligible?: boolean;
        obfuscatedEmail?: string;
      };

      if (!data.eligible) {
        setPageState("idle");
        return;
      }

      setObfuscatedEmail(data.obfuscatedEmail ?? "");
      setPageState("code_sent");
    } catch (err) {
      const apiError = err as {
        statusCode?: number;
        error?: string;
        message?: string;
      };
      setError(
        apiError.error ||
          apiError.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setIsSending(false);
    }
  }, [initialsValue]);

  const handleVerify = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError("");

      if (newPassword !== confirmPassword) {
        setError("Passwords do not match");
        return;
      }
      if (newPassword.length < 4 || newPassword.length > 16) {
        setError("Password must be 4-16 characters");
        return;
      }

      setIsSubmitting(true);

      try {
        await verifyStaffReset({ otp, newPassword, confirmPassword });
        setPageState("success");
      } catch (err) {
        const apiError = err as {
          statusCode?: number;
          error?: string;
          message?: string;
        };
        setError(
          apiError.error ||
            apiError.message ||
            "Something went wrong. Please try again."
        );
        setOtp("");
      } finally {
        setIsSubmitting(false);
      }
    },
    [otp, newPassword, confirmPassword]
  );

  // Success state
  if (pageState === "success") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-10 pb-8 text-center space-y-4">
            <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
            <div>
              <h2 className="text-xl font-semibold">Password Reset</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Your password has been updated. You can now log in with your new
                password.
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() => navigate({ to: "/select-user" })}
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3 mb-1">
            <Link
              to="/select-user"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <CardTitle className="text-2xl">Reset Password</CardTitle>
          </div>
          <CardDescription>
            {pageState === "idle" &&
              "Enter your initials to check if your account is eligible for self-service password reset."}
            {pageState === "confirm" &&
              "We'll send a verification code to the pharmacy email to confirm your identity."}
            {pageState === "code_sent" &&
              `Enter the 6-digit code sent to ${obfuscatedEmail} and choose a new password.`}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {/* Step 1: Initials input */}
          {pageState === "idle" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="initials">Your Initials</Label>
                <Input
                  id="initials"
                  type="text"
                  placeholder="e.g. JD"
                  value={initialsValue}
                  onChange={(e) =>
                    setInitialsValue(
                      e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z]/g, "")
                        .slice(0, 5)
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCheckEligibility();
                    }
                  }}
                  disabled={isChecking}
                  autoComplete="off"
                  autoFocus
                  maxLength={5}
                />
              </div>

              {notEligibleMessage && (
                <Alert>
                  <AlertDescription>{notEligibleMessage}</AlertDescription>
                </Alert>
              )}

              <Button
                className="w-full"
                onClick={handleCheckEligibility}
                disabled={isChecking || initialsValue.length < 2}
              >
                {isChecking ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Checking...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </div>
          )}

          {/* Step 2: Confirm and send code */}
          {pageState === "confirm" && (
            <div className="space-y-4">
              <div className="rounded-md bg-muted px-4 py-3 text-sm">
                Resetting password for:{" "}
                <span className="font-semibold">
                  {initialsValue.toUpperCase()}
                </span>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                className="w-full"
                onClick={handleSendCode}
                disabled={isSending}
              >
                {isSending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending code...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Send Verification Code
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Step 3: OTP + new password */}
          {pageState === "code_sent" && (
            <form
              onSubmit={handleVerify}
              autoComplete="off"
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="otp">Verification Code</Label>
                <Input
                  id="otp"
                  name="otp"
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  maxLength={6}
                  required
                  disabled={isSubmitting}
                  autoComplete="one-time-code"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <SecureInput
                  id="new-password"
                  value={newPassword}
                  onChange={setNewPassword}
                  maxLength={16}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">4-16 characters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <SecureInput
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  maxLength={16}
                  disabled={isSubmitting}
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={
                  isSubmitting ||
                  otp.length !== 6 ||
                  newPassword.length < 4 ||
                  confirmPassword.length < 4
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resetting password...
                  </>
                ) : (
                  "Reset Password"
                )}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setPageState("confirm");
                  setOtp("");
                  setError("");
                }}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center"
              >
                Didn&apos;t receive the code? Send again
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
